import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TextField } from "@mui/material";
import ExplorerShell from "../components/explorer/ExplorerShell";
import {
  ApiExplorerCard,
  ApiExplorerListRow,
} from "../components/explorer/ApiExplorerViews";
import { useExplorerUrlState } from "../hooks/useExplorerUrlState";
import { apiService } from "../api/services/apiService";
import type { API } from "../types";
import { formatApiDisplayName } from "../utils/apiDisplayName";
import {
  compareExplorerValues,
  isVisibleToExplorer,
  matchesExplorerSearch,
  paginate,
  EXPLORER_PAGE_SIZE_CARD,
  EXPLORER_PAGE_SIZE_LIST,
} from "../utils/explorerUtils";

const API_SORT_OPTIONS = [
  { id: "name", label: "Name" },
  { id: "version", label: "Version" },
  { id: "status", label: "Status" },
];

const ApiExplorerPage: React.FC = () => {
  const state = useExplorerUrlState({ defaultSort: "name" });
  const [apis, setApis] = useState<API[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versionFilter, setVersionFilter] = useState("");

  const fetchApis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiService.getApis();
      setApis(list.filter((a) => isVisibleToExplorer(a.status)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load APIs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApis();
  }, [fetchApis]);

  const processed = useMemo(() => {
    let list = apis;

    if (state.statuses.length) {
      list = list.filter((a) =>
        state.statuses.includes(a.status as (typeof state.statuses)[number]),
      );
    }

    if (versionFilter.trim()) {
      const v = versionFilter.toLowerCase();
      list = list.filter((a) =>
        (a.version ?? "").toLowerCase().includes(v),
      );
    }

    list = list.filter((a) =>
      matchesExplorerSearch(state.debouncedQ, [
        a.name,
        formatApiDisplayName(a.name),
        a.description,
        a.version,
      ]),
    );

    list = [...list].sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (state.sort) {
        case "version":
          av = a.version ?? "";
          bv = b.version ?? "";
          break;
        case "status":
          av = a.status;
          bv = b.status;
          break;
        default:
          av = a.name;
          bv = b.name;
      }
      return compareExplorerValues(av, bv, state.sortDir);
    });

    return list;
  }, [apis, state.debouncedQ, state.sort, state.sortDir, state.statuses, versionFilter]);

  const pageSize =
    state.layout === "list"
      ? EXPLORER_PAGE_SIZE_LIST
      : EXPLORER_PAGE_SIZE_CARD;
  const paginated = paginate(processed, state.page, pageSize);

  return (
    <ExplorerShell
      pageTitle="API Explorer"
      pageDescription="Browse and explore APIs available to you. Read-only — view specifications and metadata."
      loading={loading}
      error={error}
      items={paginated}
      totalFiltered={processed.length}
      state={state}
      sortOptions={API_SORT_OPTIONS}
      emptyMessage="No APIs are available to explore."
      getItemKey={(a) => a.uuid}
      renderCard={(a) => <ApiExplorerCard api={a} />}
      renderListRow={(a) => <ApiExplorerListRow api={a} />}
      onClearAllExtra={() => setVersionFilter("")}
      filterSidebarExtra={
        <TextField
          fullWidth
          size="small"
          label="Version contains"
          value={versionFilter}
          onChange={(e) => {
            setVersionFilter(e.target.value);
            state.setPage(1);
          }}
          sx={{ mt: 2 }}
        />
      }
    />
  );
};

export default ApiExplorerPage;
